import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Medal, User, Gamepad2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leaderboard({ limit = 10, showTitle = true }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const [leaderboardRes, statsRes] = await Promise.all([
        axios.get(`${API}/player/leaderboard?limit=${limit}`),
        axios.get(`${API}/player/stats`),
      ]);
      setLeaderboard(leaderboardRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-300" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-zinc-500 font-bold">{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return "bg-gradient-to-r from-yellow-500/20 to-transparent border-yellow-500/30";
      case 2:
        return "bg-gradient-to-r from-gray-400/20 to-transparent border-gray-400/30";
      case 3:
        return "bg-gradient-to-r from-amber-600/20 to-transparent border-amber-600/30";
      default:
        return "bg-zinc-800/30 border-white/5";
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 bg-zinc-800/50 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="leaderboard">
      {showTitle && (
        <div className="flex items-center gap-3 mb-6">
          <Trophy className="w-8 h-8 text-[#fccf1f]" />
          <div>
            <h2 className="text-2xl font-nunito font-bold text-[#fccf1f]">
              Ranking Global
            </h2>
            {stats && (
              <p className="text-zinc-400 text-sm">
                {stats.total_players} jogadores • {stats.total_games} partidas jogadas
              </p>
            )}
          </div>
        </div>
      )}

      {leaderboard.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/40 rounded-xl border border-white/10">
          <Gamepad2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">Nenhum jogador no ranking ainda</p>
          <p className="text-zinc-500 text-sm mt-1">Seja o primeiro a jogar!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {leaderboard.map((player) => (
            <div
              key={player.user_id}
              className={`flex items-center gap-4 p-4 rounded-lg border transition-all hover:scale-[1.01] ${getRankBg(player.rank)}`}
              data-testid={`leaderboard-entry-${player.rank}`}
            >
              <div className="flex-shrink-0">
                {getRankIcon(player.rank)}
              </div>
              
              <div className="flex-shrink-0">
                {player.picture ? (
                  <img
                    src={player.picture}
                    alt={player.display_name || player.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-white/20"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                    <User className="w-5 h-5 text-zinc-400" />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-nunito font-bold text-white truncate">
                  {player.display_name || player.name}
                </p>
                <p className="text-xs text-zinc-500">
                  {player.games_played} {player.games_played === 1 ? 'partida' : 'partidas'}
                </p>
              </div>

              <div className="text-right">
                <p className="font-nunito font-black text-xl text-[#fccf1f]">
                  {player.total_score}
                </p>
                <p className="text-xs text-zinc-500">
                  Melhor: {player.best_score}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
